import React, { useState, useEffect } from 'react';
import { renderIcon } from '../../utils/iconRenderer';
import { FaSearch, FaComment } from 'react-icons/fa';
import { db } from '../../firebase';
import { collection, onSnapshot, query, orderBy, where } from 'firebase/firestore';

const THREADS_COL = 'private_threads';
const MESSAGES_COL = 'private_messages';

const PrivateChatModule = () => {
    const [threads, setThreads] = useState([]);
    const [messages, setMessages] = useState([]);
    const [users, setUsers] = useState([]); // All users for identity resolution
    const [selectedThread, setSelectedThread] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);

    // Fetch all users for name lookup
    useEffect(() => {
        const unsubscribe = onSnapshot(collection(db, 'users'), (snapshot) => {
            const data = snapshot.docs.map(doc => ({ 
                id: doc.id, 
                ...doc.data(),
                displayName: doc.data().pseudonym || doc.data().handle || `${doc.data().firstName || ''} ${doc.data().lastName || ''}`.trim() || doc.id
            }));
            setUsers(data);
        });
        return () => unsubscribe();
    }, []);

    // Identity resolver
    const resolveIdentity = (uid, fallback) => {
        const found = users.find(u => u.id === uid);
        return found?.displayName || fallback || uid;
    };

    // Fetch all private threads
    useEffect(() => {
        const q = query(collection(db, THREADS_COL), orderBy('lastMessageAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setThreads(data);
            setLoading(false);
        }, (error) => {
            console.error('Fetch error:', error);
            // Fallback without orderBy
            onSnapshot(collection(db, THREADS_COL), (snapshot) => {
                const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setThreads(data);
                setLoading(false);
            });
        });
        return () => unsubscribe();
    }, []);

    // Fetch messages
    useEffect(() => {
        if (!selectedThread) return;
        setMessages([]);

        const q = query(
            collection(db, MESSAGES_COL),
            where('threadId', '==', selectedThread.id)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const msgs = snapshot.docs
                .map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    _ts: doc.data().createdAt?.seconds || 0,
                }))
                .sort((a, b) => a._ts - b._ts);
            setMessages(msgs);
        });

        return () => unsubscribe();
    }, [selectedThread]);

    // Dynamic Resolution for rendering
    const processedThreads = threads.map(t => {
        const pNames = t.participantNames || [];
        const u1Id = t.participants?.[0];
        const u2Id = t.participants?.[1];

        return {
            ...t,
            user1: resolveIdentity(u1Id, pNames[0] || t.postAuthorName),
            user2: resolveIdentity(u2Id, pNames[1]),
            lastMsg: t.lastMessageText || t.lastMessage || 'No messages yet',
            time: t.lastMessageAt
                ? new Date(t.lastMessageAt.seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                : t.lastMessageTime 
                    ? new Date(t.lastMessageTime.seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    : '',
        };
    });

    const filteredThreads = processedThreads.filter(t =>
        String(t.user1).toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(t.user2).toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(t.groupName).toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(t.lastMsg).toLowerCase().includes(searchTerm.toLowerCase())
    );

    const processedMessages = messages.map(msg => ({
        ...msg,
        content: msg.content || msg.text || msg.message || '',
        senderName: resolveIdentity(msg.senderId || msg.authorId, msg.senderName || msg.authorName),
        date: msg.createdAt
            ? new Date(msg.createdAt.seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            : '',
    }));

    // Find the currently selected thread but with resolved names
    const activeResolvedThread = selectedThread ? processedThreads.find(t => t.id === selectedThread.id) : null;

    return (
        <div className="content-section chat-module fade-in" style={{ height: 'calc(100vh - 180px)', display: 'flex', flexDirection: 'column' }}>
            <div className="chat-layout" style={{ flex: 1, height: '100%', overflow: 'hidden' }}>
                {/* Threads Sidebar */}
                <div className="chat-sidebar-premium" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <div className="chat-sidebar-header">
                        <h3>Private Chats</h3>
                        <div className="search-bar-mini">
                            <FaSearch className="search-icon-mini" />
                            <input
                                type="text"
                                placeholder="Search chats..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="chat-list custom-scrollbar" style={{ flex: 1, overflowY: 'auto', padding: '10px' }}>
                        {loading && (
                            <div style={{ padding: '20px', textAlign: 'center' }}>
                                <div className="pulse-loader" style={{ width: '20px', height: '20px', margin: '0 auto' }}></div>
                                <p style={{ fontSize: '12px', marginTop: '8px', color: 'var(--text-tertiary)' }}>Loading chats...</p>
                            </div>
                        )}
                        {!loading && filteredThreads.length === 0 && (
                            <div style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--text-tertiary)' }}>
                                <div style={{ fontSize: '32px', marginBottom: '12px' }}>💬</div>
                                <div style={{ fontWeight: 600, marginBottom: '6px', fontSize: '13px' }}>No private chats found</div>
                                <div style={{ fontSize: '11px', lineHeight: 1.6 }}>
                                    Search for a different user or check the <code>private_threads</code> collection.
                                </div>
                            </div>
                        )}
                        {filteredThreads.map(thread => (
                            <div
                                key={thread.id}
                                className={`chat-group-item-modern ${selectedThread?.id === thread.id ? 'active' : ''}`}
                                onClick={() => setSelectedThread(thread)}
                            >
                                <div className="dual-avatar-modern">
                                    <div className="avatar-main">{renderIcon('male', 24)}</div>
                                    <div className="avatar-sub">{renderIcon('female', 18)}</div>
                                </div>
                                <div className="group-content-modern">
                                    <div className="group-name-row-modern">
                                        <span className="group-name-modern">
                                            {thread.user1} vs {thread.user2}
                                        </span>
                                    </div>
                                    <span className="group-preview-modern">{thread.lastMsg}</span>
                                </div>
                                <div className="chat-item-meta" style={{ marginLeft: 'auto', fontSize: '11px', color: 'var(--text-tertiary)', textAlign: 'right' }}>
                                    <span>{thread.time}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Main Chat Area */}
                <div className="chat-window" style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                    {activeResolvedThread ? (
                        <>
                            <div className="chat-window-header">
                                <div className="header-group-info">
                                    <div className="header-text">
                                        <h4 style={{ margin: 0 }}>
                                            Conversation Audit: {activeResolvedThread.user1} & {activeResolvedThread.user2}
                                        </h4>
                                        <span className="header-stats" style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>
                                            Thread ID: {activeResolvedThread.id}
                                        </span>
                                    </div>
                                </div>
                                <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', background: 'var(--bg-secondary)', padding: '4px 12px', borderRadius: '20px' }}>
                                    ReadOnly Admin Access
                                </div>
                            </div>

                            <div className="chat-messages-container custom-scrollbar" style={{ flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                {processedMessages.length > 0 ? (
                                    processedMessages.map((msg, idx) => {
                                        const isRight = msg.senderId === activeResolvedThread.participants?.[1];
                                        return (
                                            <div key={msg.id || idx} className={`chat-message-item ${isRight ? 'msg-right' : ''}`} style={{ alignSelf: isRight ? 'flex-end' : 'flex-start', maxWidth: '80%' }}>
                                                <div className="msg-content-wrapper" style={{ 
                                                    padding: '12px 18px', 
                                                    background: isRight ? 'var(--primary-blue)' : 'var(--bg-secondary)', 
                                                    color: isRight ? '#fff' : 'var(--text-primary)',
                                                    borderRadius: isRight ? '20px 6px 20px 20px' : '6px 200px 20px 20px', // Re-adjusting for premium feel
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                                    transition: 'transform 0.2s ease'
                                                }}>
                                                    <div className="msg-header" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', gap: '30px', alignItems: 'center' }}>
                                                        <span className="msg-author" style={{ 
                                                            fontWeight: 800, 
                                                            fontSize: '12px', 
                                                            color: isRight ? 'rgba(255,255,255,0.9)' : 'var(--primary-blue)' 
                                                        }}>{msg.senderName}</span>
                                                        <span className="msg-time" style={{ 
                                                            fontSize: '10px', 
                                                            color: isRight ? 'rgba(255,255,255,0.7)' : 'var(--text-tertiary)' 
                                                        }}>{msg.date}</span>
                                                    </div>
                                                    <div className="msg-body">
                                                        {msg.mediaUrl && (() => {
                                                            const isVideo = msg.mediaType === 'video' || 
                                                                           (typeof msg.mediaUrl === 'string' && 
                                                                           (msg.mediaUrl.toLowerCase().includes('.mp4') || 
                                                                           msg.mediaUrl.toLowerCase().includes('.mov') || 
                                                                           msg.mediaUrl.toLowerCase().includes('.webm') ||
                                                                           msg.mediaUrl.toLowerCase().includes('video')));
                                                            
                                                            return isVideo ? (
                                                                <video src={msg.mediaUrl} controls style={{ maxWidth: '100%', borderRadius: '8px', marginBottom: '8px', maxHeight: '300px' }} />
                                                            ) : (
                                                                <img src={msg.mediaUrl} alt="media" style={{ maxWidth: '100%', borderRadius: '8px', marginBottom: '8px', cursor: 'pointer' }} onClick={() => window.open(msg.mediaUrl, '_blank')} />
                                                            );
                                                        })()}
                                                        <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.5' }}>{msg.content}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="empty-chat" style={{ textAlign: 'center', marginTop: '100px', color: 'var(--text-tertiary)' }}>
                                        <FaComment size={40} style={{ opacity: 0.2 }} />
                                        <p>No messages recorded in this thread yet.</p>
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="no-chat-selected" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-tertiary)' }}>
                            <div style={{ textAlign: 'center' }}>
                                <span style={{ fontSize: '64px' }}>💬</span>
                                <h3>Select a Chat</h3>
                                <p>Select a conversation from the sidebar to view audit logs.</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PrivateChatModule;
